import { useEffect, useState } from "react";
import { fmtMoney } from "@/lib/money";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Check, Clock, Sparkles, CreditCard, Mail, CalendarClock, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
// import { LanguagePreferenceCard } from "@/components/LanguagePreferenceCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function Account() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");
  const REASONS = ["Troppo caro", "Mi mancano delle funzioni", "Difficile da usare", "Ho chiuso il locale", "Uso un altro servizio", "Altro"];

  useEffect(() => { (async () => setData((await api.get("/account")).data))(); }, []);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account", { data: { reason, reason_text: reasonText } });
      toast.success("Account eliminato. Arrivederci!");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  const { user, subscription, plan_features, transactions } = data;
  const paid = transactions.filter((t) => t.status === "paid");

  return (
    <div className="space-y-8 max-w-4xl" data-testid="account-page">
      <div>
        <h1 className="font-display font-bold text-4xl">Il mio account</h1>
        <p className="text-gray-500 mt-1">Dati del profilo, abbonamento e storico acquisti.</p>
      </div>

      {/* Profilo */}
      <div className="rounded-2xl mo-card p-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-[#5aa469] text-white flex items-center justify-center text-lg font-semibold overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.name?.[0]?.toUpperCase() || "U")}
          </div>
          <div className="min-w-0">
            <p className="font-display font-semibold text-xl">{user.name}</p>
            <p className="text-sm text-gray-500 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{user.email}</p>
          </div>
        </div>
      </div>

      {/* Abbonamento attuale */}
      <div className="rounded-2xl mo-card p-6" data-testid="current-subscription">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display font-semibold text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#5aa469]" />Abbonamento</h2>
          <Link to="/dashboard/billing"><Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="manage-plan"><CreditCard className="h-4 w-4 mr-2" />Gestisci piano</Button></Link>
        </div>

        {subscription?.status === "active" ? (
          <>
            <div className="flex items-center gap-3 mb-5">
              <span className="text-2xl font-display font-bold" data-testid="active-plan-name">Piano {subscription.name}</span>
              <span className="text-xs font-medium text-[#4a8a56] bg-[#5aa469]/12 px-2.5 py-1 rounded-full">Attivo</span>
            </div>
            {(subscription.started_at || subscription.renews_at) && (
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {subscription.started_at && (
                  <div className="rounded-xl bg-gray-50 px-4 py-3" data-testid="sub-started">
                    <p className="text-xs text-gray-500">Attivato il</p>
                    <p className="font-semibold">{fmtDate(subscription.started_at)}</p>
                  </div>
                )}
                {subscription.renews_at && (
                  <div className="rounded-xl bg-[#5aa469]/8 border border-[#5aa469]/30 px-4 py-3" data-testid="sub-renews">
                    <p className="text-xs text-gray-500 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-[#5aa469]" />{subscription.lookup_key?.endsWith("_daily") ? "Scade il" : "Prossimo rinnovo"}</p>
                    <p className="font-semibold">{fmtDate(subscription.renews_at)}</p>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm font-medium text-gray-700 mb-3">Funzioni incluse nel tuo piano</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {plan_features.map((f) => (
                <li key={f.label} className="flex items-start gap-2 text-sm" data-testid="plan-feature">
                  {f.live
                    ? <Check className="h-4 w-4 text-[#5aa469] mt-0.5 shrink-0" />
                    : <Clock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                  <span className={f.live ? "text-gray-700" : "text-gray-500"}>
                    {f.label}{!f.live && <span className="text-amber-600 text-xs ml-1">(in arrivo)</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-4">Le voci con <Clock className="h-3 w-3 inline text-amber-500" /> "in arrivo" fanno parte del tuo piano e saranno attivate a breve.</p>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Non hai ancora un abbonamento attivo.</p>
            <Link to="/dashboard/billing"><Button className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="choose-plan">Scegli un piano</Button></Link>
          </div>
        )}
      </div>

      {/* Storico acquisti */}
      <div className="rounded-2xl mo-card overflow-hidden" data-testid="purchase-history">
        <div className="px-6 py-4 border-b border-gray-100"><h2 className="font-display font-semibold text-2xl">Storico acquisti</h2></div>
        {paid.length === 0 ? (
          <p className="text-center text-gray-400 py-10">Nessun acquisto registrato.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Data</TableHead>
                  <TableHead>Piano</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead className="text-right">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paid.map((t, i) => (
                  <TableRow key={`${t.created_at}-${t.lookup_key}-${i}`} className="hover:bg-gray-50">
                    <TableCell className="text-gray-600">{fmtDate(t.created_at)}</TableCell>
                    <TableCell className="font-medium text-gray-900">{t.plan_name}</TableCell>
                    <TableCell className="text-right text-gray-900">{fmtMoney(t.amount, t.currency || "eur")}</TableCell>
                    <TableCell className="text-right"><span className="text-xs font-medium text-[#4a8a56] bg-[#5aa469]/12 px-2.5 py-1 rounded-full">Pagato</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* <LanguagePreferenceCard /> — disattivata: dashboard solo in italiano */}

      <div className="rounded-2xl border border-red-200 bg-red-50/50 p-6" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-xl text-red-700">Elimina account</h3>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              Questa azione è <strong>irreversibile</strong>. Verranno eliminati definitivamente il tuo account, il ristorante, il menù e le immagini. L'eventuale abbonamento attivo verrà annullato.
            </p>
            <AlertDialog onOpenChange={() => setConfirmText("")}>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700" data-testid="open-delete-account">
                  <Trash2 className="h-4 w-4 mr-2" /> Elimina il mio account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-2xl">Sei sicuro?</AlertDialogTitle>
                  <AlertDialogDescription>
                    L'account e tutti i dati collegati saranno eliminati per sempre e l'abbonamento verrà annullato. Per confermare, scrivi <strong>ELIMINA</strong> qui sotto.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2" data-testid="delete-reason-box">
                  <p className="text-sm font-medium">Perché te ne vai? <span className="text-gray-400 font-normal">(ci aiuta a migliorare)</span></p>
                  <div className="flex flex-wrap gap-1.5">
                    {REASONS.map((r) => <button key={r} type="button" onClick={() => setReason(r)} className={`rounded-full px-3 py-1.5 text-xs font-medium border ${reason === r ? "bg-red-600 text-white border-red-600" : "bg-white border-gray-200 hover:border-red-300"}`} data-testid={`delete-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}>{r}</button>)}
                  </div>
                  <textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} maxLength={500} rows={2} placeholder="Vuoi dirci qualcosa in più? (facoltativo)" className="w-full rounded-lg border border-gray-200 p-2 text-sm" data-testid="delete-reason-text" />
                </div>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="ELIMINA" className="h-11 rounded-lg" data-testid="delete-confirm-input" />
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { if (confirmText.trim().toUpperCase() !== "ELIMINA") { e.preventDefault(); toast.error("Scrivi ELIMINA per confermare"); return; } deleteAccount(); }}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                    data-testid="confirm-delete-account">
                    {deleting ? "Eliminazione…" : "Elimina definitivamente"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
